import useDiscoveryFetch from './useDiscoveryFetch';
import MobileJobsSortDrawer from './MobileJobsSortDrawer';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Table, Form, Row, Col, Card, Overlay, Tooltip } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import Container from 'react-bootstrap/Container';
import Pagination from 'react-bootstrap/Pagination';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import api from '../../api/axiosInstance';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import defaultLogo from '../../assets/img/ic-img.svg';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import DiscoveryMap from './DiscoveryMap';
import DiscoveryFilterBar from './DiscoveryFilterBar';
import DiscoveryEmptyState from './DiscoveryEmptyState';
import MobileDiscoveryHeading from './MobileDiscoveryHeading';
import MobileDiscoverySearchToolbar from './MobileDiscoverySearchToolbar';
import MobileDiscoveryViewSwitch from './MobileDiscoveryViewSwitch';
import MobileCompanyCard from './MobileCompanyCard';
import NavTabs from '../../NavTabs';
import useMobileDiscoveryNavDocking from './useMobileDiscoveryNavDocking';
import useMobileLoadMore from './useMobileLoadMore';
import MobileLoadMoreFooter from './MobileLoadMoreFooter';
import MobileAppliedChips from './MobileAppliedChips';
import { captureDiscoveryFilters, buildDiscoveryFilterChipLabels } from './discoveryMobileFilters';
import { useMobileAiPageContext, useDesktopAiInputRef } from '../../MobileAiAssistantContext';
import icLink from '../../assets/img/ic-link-square.svg';
import icMail from '../../assets/img/ic-mail-send.svg';
import icPhone from '../../assets/img/ic-smart-phone.svg';
import icFilter from '../../assets/img/ic-filter.svg';
import icSort from '../../assets/img/ic-sort.svg';
import icJobSearch from '../../assets/img/ic-job-search.svg';
import icHeartEmpty from '../../assets/img/ic-heart-empty.svg';
import icHeartFull from '../../assets/img/ic-heart-full.svg';
import DiscoveryAiDesktopCard from './DiscoveryAiDesktopCard';
import countriesData from '../../data/countries';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';

const PAGE_DISCOVERY_TYPE = 'jobs';

// CONTRACT: this mapping must stay identical to AI_INDUSTRY_GROUPS in the backend's
// discoveryAiContextService.js (and in DiscoveryCompanyList.js). The backend uses it to
// decide which companies are in the AI's matchedCompanies; this page uses the identical
// mapping to reproduce that same result set. Update all three together.
const AI_INDUSTRY_GROUPS = {
    'financial services': ['64', '65', '66'],
    'software': ['62', '63'],
};

// Mobile map drill-down: country bubbles → city bubbles → company markers.
// Module-level so the grouping functions keep a stable identity across renders.
const getCompanyCountryKey = (company) => String(company.country || '').trim();
const getCompanyCityKey = (company) => String(company.cityRegion || '').trim();

const MOBILE_MAP_OVERVIEW_MAX_ZOOM = 4;
const MOBILE_MAP_COUNTRY_FIT_ZOOM = 8;
const MOBILE_MAP_CITY_FIT_ZOOM = 11.5;
// A user zoom-out below these steps the drill-down back one level.
const MOBILE_MAP_CITY_MODE_MIN_ZOOM = 8.5;
const MOBILE_MAP_COUNTRY_MODE_MIN_ZOOM = 4.5;
// Company level mirrors DiscoveryCountryCompanyList: from this clusterZoom markers
// render individually, and overlap-cluster taps may zoom deep enough to separate them.
const MOBILE_MAP_COMPANY_UNCLUSTER_ZOOM = 9;
const MOBILE_MAP_COMPANY_CLUSTER_CLICK_ZOOM = 14;

const DiscoveryJobsList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // Keep the last explicitly applied comparator when fresh API data arrives.
    const refreshSortRef = useRef(null);
    const [companies, setCompanies] = useState([]);
    const defaultCompaniesOrderRef = useRef([]);
    const [favorites, setFavorites] = useState(new Set());
    const [activeCityNames, setActiveCityNames] = useState(new Set());
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [companiesPerPage, setCompaniesPerPage] = useState(10);
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [industriesData, setIndustriesData] = useState([]);

    // Initialized once from the URL; ?industry=Financial Services stays a group match
    // for the life of the page rather than a single selectedIndustry code.
    const [aiIndustryGroup, setAiIndustryGroup] = useState(() => {
        const rawParam = String(searchParams.get('industry') || '').trim();
        return AI_INDUSTRY_GROUPS[rawParam.toLowerCase()] ? rawParam : null;
    });

    const clearAiIndustryGroup = () => {
        setAiIndustryGroup(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('industry');
            return next;
        }, { replace: true });
    };

    const [aiQuery, setAiQuery] = useState('');
    const [aiResult, setAiResult] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiError, setAiError] = useState('');
    const [selectedAiSources, setSelectedAiSources] = useState(['abroadify']);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || '');
    const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
    const [selectedStudyDirection, setSelectedStudyDirection] = useState('');
    const [selectedCompanySize, setSelectedCompanySize] = useState('');
    const [selectedCompanyRating, setSelectedCompanyRating] = useState('');
    const [selectedEnglishSpeaking, setSelectedEnglishSpeaking] = useState(
        searchParams.get('englishSpeaking') === 'true' ? true : null
    );
    const [selectedWorldTraveler, setSelectedWorldTraveler] = useState(null);
    const [selectedStudentJobAreas, setSelectedStudentJobAreas] = useState(
        searchParams.get('studentFriendly') === 'true' ? true : null
    );
    const [selectedInternship, setSelectedInternship] = useState(
        searchParams.get('internship') === 'true' ? true : null
    );
    const [selectedFavorites, setSelectedFavorites] = useState(null);
    const [overlayTarget, setOverlayTarget] = useState(null);
    const [showCopied, setShowCopied] = useState(false);
    const copyTimeoutRef = useRef(null);
    const contactRefs = useRef({});
    const [savedFilterSets, setSavedFilterSets] = useState([]);
    const [selectedFilterSet, setSelectedFilterSet] = useState('default');
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [showMobileSort, setShowMobileSort] = useState(false);
    const [draftSortField, setDraftSortField] = useState(null);
    const [draftSortDirection, setDraftSortDirection] = useState(null);
    const [appliedMobileFilters, setAppliedMobileFilters] = useState(() => captureDiscoveryFilters({
        searchQuery,
        selectedCountry,
        selectedCity,
        selectedIndustry,
        aiIndustryGroup,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    }));
    const { isMobileNavDocked, isMobileViewport, mobileMapRowRef } = useMobileDiscoveryNavDocking();

    // Mobile company-card rendering path (structural test) — media-breakpoint-down(md).
    const isMobile = useMediaQuery({
        query: '(max-width: 767.98px)',
    });

    // Stable reference — prevents DiscoveryMap from resetting viewport on every render
    const mapInitialCenter = useMemo(() => ({ latitude: 50, longitude: 10, zoom: 3.4 }), []);

    // Mobile map drill-down selection. Map-only state: taps on country/city bubbles
    // set it, zooming out clears it — it never touches the page filters.
    const [mapDrill, setMapDrill] = useState({ country: '', city: '' });
    const mapPrevZoomRef = useRef(null);

    // Changing the page filters restarts the drill-down from the matching level.
    useEffect(() => {
        setMapDrill((prev) => (prev.country || prev.city ? { country: '', city: '' } : prev));
    }, [selectedCountry, selectedCity]);

    const filteredCompanies = companies.filter((company) => {
        const normalizeStudyDirection = (val) => {
            const str = String(val).trim();
            return str
                .replace(/^\d+(?:\.\d+)?\s*[-–—]?\s*/, '')
                .replace(/([.:])\s*\d+(?:\.\d+)?\s*[-–—]?\s*/g, '$1 ')
                .replace(/^\(\s*/, '')
                .replace(/\)\s*$/, '')
                .replace(/\s{2,}/g, ' ')
                .trim()
                .toLowerCase();
        };

        const matchesSearch =
            !searchQuery ||
            company.title?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCountry =
            !selectedCountry || company.country === selectedCountry;

        const matchesCity =
            !selectedCity ||
            String(company.cityRegion || '').trim() === String(selectedCity || '').trim();

        const aiIndustryGroupCodes = aiIndustryGroup
            ? AI_INDUSTRY_GROUPS[aiIndustryGroup.toLowerCase()]
            : null;

        const matchesIndustry = aiIndustryGroupCodes
            ? (Array.isArray(company.industries) &&
                company.industries.some((entry) =>
                    aiIndustryGroupCodes.includes(String(entry?.industry || '').trim())
                ))
            : (
                !selectedIndustry ||
                (Array.isArray(company.industries) &&
                    company.industries.some((entry) => entry?.industry === selectedIndustry))
            );

        const matchesStudyDirection =
            !selectedStudyDirection ||
            (() => {
                const rawStudyDirections = company.studyFields?.preferredStudyDirections;
                if (Array.isArray(rawStudyDirections)) {
                    return rawStudyDirections.some(
                        (direction) =>
                            normalizeStudyDirection(direction) === normalizeStudyDirection(selectedStudyDirection)
                    );
                }
                if (typeof rawStudyDirections === 'string') {
                    return rawStudyDirections
                        .split(',')
                        .map((direction) => normalizeStudyDirection(direction))
                        .includes(normalizeStudyDirection(selectedStudyDirection));
                }
                return false;
            })();

        const matchesEnglishSpeaking =
            selectedEnglishSpeaking === null ||
            company.openedEnglishSpeaking === true;

        const matchesWorldTraveler =
            selectedWorldTraveler === null ||
            company.openedWorldTraveler === true;

        const matchesStudentJobAreas =
            selectedStudentJobAreas === null ||
            (
                Array.isArray(company.studentJobAreas)
                    ? company.studentJobAreas.length > 0
                    : Boolean(company.studentJobAreas)
            );

        const matchesInternship =
            selectedInternship === null ||
            Number(company.openJobsIntern) > 0;

        const matchesFavorites =
            selectedFavorites === null ||
            favorites.has(String(company._id));

        return (
            matchesSearch &&
            matchesCountry &&
            matchesCity &&
            matchesIndustry &&
            matchesStudyDirection &&
            matchesEnglishSpeaking &&
            matchesWorldTraveler &&
            matchesStudentJobAreas &&
            matchesInternship &&
            matchesFavorites
        );
    });

    const indexOfLastCompany = currentPage * companiesPerPage;
    const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
    const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);

    const mobileFilterKey = JSON.stringify({
        searchQuery,
        selectedCountry,
        selectedCity,
        selectedIndustry,
        aiIndustryGroup,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
        sortField,
        sortDirection,
    });
    const mobilePagination = useMobileLoadMore({
        items: filteredCompanies,
        filterKey: mobileFilterKey,
        storageKey: 'discovery-jobs-mobile-list',
        isMobile,
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchQuery,
        selectedCountry,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    ]);

    const buildCurrentFiltersPayload = () => ({
        searchQuery,
        selectedCountry,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    });

    const applySavedFilterSet = (filters = {}) => {
        setSearchQuery(filters.searchQuery || '');
        setSelectedCountry(filters.selectedCountry || '');
        setSelectedCity(filters.selectedCity || '');
        setSelectedIndustry(filters.selectedIndustry || '');
        setSelectedStudyDirection(filters.selectedStudyDirection || '');
        setSelectedCompanySize(filters.selectedCompanySize || '');
        setSelectedCompanyRating(filters.selectedCompanyRating || '');
        setSelectedEnglishSpeaking(filters.selectedEnglishSpeaking === true ? true : null);
        setSelectedWorldTraveler(filters.selectedWorldTraveler === true ? true : null);
        setSelectedStudentJobAreas(filters.selectedStudentJobAreas === true ? true : null);
        setSelectedInternship(filters.selectedInternship === true ? true : null);
        setSelectedFavorites(filters.selectedFavorites === true ? true : null);
    };

    const clearJobsFilters = () => {
        applySavedFilterSet({});
        clearAiIndustryGroup();
    };

    // Mobile Filters drawer's "Clear all" — search now lives in the mobile toolbar,
    // independent of the drawer, so this must reset every filter except searchQuery.
    const clearJobsFiltersPreserveSearch = () => {
        applySavedFilterSet({ searchQuery });
        clearAiIndustryGroup();
    };

    const handleSaveFilters = async (filterSetName) => {
        try {
            const trimmedName = String(filterSetName || '').trim();
            if (!trimmedName) return;

            const payload = {
                page: 'jobs',
                name: trimmedName,
                filters: buildCurrentFiltersPayload(),
            };

            const res = await api.post('/api/discovery-filter-sets', payload);
            const savedFilterSet = res.data;

            setSavedFilterSets((prev) => {
                const withoutDuplicate = prev.filter(
                    (item) => item._id !== savedFilterSet._id && item.name !== savedFilterSet.name
                );
                return [savedFilterSet, ...withoutDuplicate];
            });

            setSelectedFilterSet(savedFilterSet._id);
        } catch (error) {
            console.error('Error saving discovery filter set:', error);
        }
    };

    // preserveSearch: mobile deletes a saved preset without touching the live
    // toolbar search, since Search is independent of staged/saved filter state there.
    // Desktop keeps the previous full-reset behavior.
    const handleDeleteFilterSet = async (filterSetId, { preserveSearch = false } = {}) => {
        if (!filterSetId || filterSetId === 'default') return;

        try {
            await api.delete(`/api/discovery-filter-sets/${filterSetId}`);

            const wasSelected = String(selectedFilterSet) === String(filterSetId);

            setSavedFilterSets((prev) =>
                prev.filter((filterSet) => String(filterSet._id) !== String(filterSetId))
            );

            if (wasSelected) {
                setSelectedFilterSet('default');
                applySavedFilterSet(preserveSearch ? { searchQuery } : {});
            }
        } catch (err) {
            console.error('Error deleting filter set:', err);
        }
    };

    const handleFilterSetChange = (value) => {
        setSelectedFilterSet(value);

        if (value === 'default') {
            clearJobsFilters();
            return;
        }

        const selectedSavedFilterSet = savedFilterSets.find((item) => item._id === value);
        if (selectedSavedFilterSet?.filters) {
            applySavedFilterSet(selectedSavedFilterSet.filters);
        }
    };

    useEffect(() => {
        trackEvent(ANALYTICS_EVENTS.DISCOVERY_OPENED, {
            discoveryType: 'companies',
            viewMode: 'jobs',
            level: 1,
            source: 'companies_jobs_toggle',
        });
    }, []);

    useDiscoveryFetch(({ signal, isCurrent }) => {
        const fetchData = async () => {
            try {
                const [jobsRes, industriesRes, filterSetsRes, currentUserRes, citiesRes] = await Promise.all([
                    api.get('/api/discovery/jobs', { signal }),
                    api.get('/api/nace', { signal }),
                    api.get('/api/discovery-filter-sets?page=jobs', { signal }),
                    api.get('/api/users/me', { signal }),
                    api.get('/api/discovery/cities', { signal }),
                ]);
                if (!isCurrent()) return;

                const safeCompanies = Array.isArray(jobsRes.data) ? jobsRes.data : [];
                const safeIndustries = Array.isArray(industriesRes.data) ? industriesRes.data : [];
                const safeFilterSets = Array.isArray(filterSetsRes.data) ? filterSetsRes.data : [];
                const safeFavoriteCompanies = Array.isArray(currentUserRes?.data?.favoriteCompanies)
                    ? currentUserRes.data.favoriteCompanies.map((id) => String(id))
                    : [];
                const safeCities = Array.isArray(citiesRes.data) ? citiesRes.data : [];

                defaultCompaniesOrderRef.current = [...safeCompanies];
                setCompanies(refreshSortRef.current ? [...safeCompanies].sort(refreshSortRef.current) : safeCompanies);
                setIndustriesData(safeIndustries);
                setSavedFilterSets(safeFilterSets);
                setFavorites(new Set(safeFavoriteCompanies));
                setActiveCityNames(new Set(safeCities.map((c) => c.cityName)));
            } catch (error) {
                if (!isCurrent()) return;
                console.error('Error fetching discovery jobs data:', error);
            }
        };

        fetchData();
    }, []);

    // Resolves an industry display label (from a URL ?industry= param or directly from
    // an AI chip's structuredFilters.industry) to what selectedIndustry/aiIndustryGroup
    // actually filter on: a known AI_INDUSTRY_GROUPS key stays a group match; anything
    // else is fuzzy-matched against the NACE list to a single code. No match -> no-op.
    const resolveIndustryLabel = (label) => {
        const trimmedLabel = String(label || '').trim();
        if (!trimmedLabel) return;

        const normalizedLabel = trimmedLabel.toLowerCase();

        if (AI_INDUSTRY_GROUPS[normalizedLabel]) {
            setAiIndustryGroup(trimmedLabel);
            return;
        }

        if (industriesData.length === 0) return;

        const matchedIndustry = industriesData.find((industry) => {
            const normalizedName = String(industry.name || '').trim().toLowerCase();
            return (
                normalizedName === normalizedLabel ||
                normalizedName.includes(normalizedLabel) ||
                normalizedLabel.includes(normalizedName)
            );
        });

        if (matchedIndustry) {
            setSelectedIndustry(String(matchedIndustry.code));
        }
    };

    // One-shot: resolve an ?industry= query param the same way. Only runs once
    // industriesData has loaded, and only ever applies once.
    const appliedIndustryParamRef = useRef(false);

    useEffect(() => {
        if (appliedIndustryParamRef.current) return;
        if (industriesData.length === 0) return;

        appliedIndustryParamRef.current = true;

        const industryParam = searchParams.get('industry');
        if (!industryParam) return;

        // Broad AI industry groups are already applied via the aiIndustryGroup
        // initializer above; only the single-code fuzzy match needs to run here.
        if (AI_INDUSTRY_GROUPS[industryParam.trim().toLowerCase()]) return;

        resolveIndustryLabel(industryParam);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industriesData, searchParams]);

    const getCountryIso2ByName = (countryName) => {
        if (!countryName) return '';

        const normalizedName = String(countryName).trim().toLowerCase();
        const match = countriesData.find(
            (country) => String(country.name || '').trim().toLowerCase() === normalizedName
        );

        return match ? match.id : '';
    };

    const aiSourceOptions = [
        { value: 'abroadify', label: 'Abroadify' },
        // { value: 'wikipedia', label: 'Wikipedia' },
        // { value: 'government', label: 'Government' },
        // { value: 'universities', label: 'Universities' },
        // { value: 'companies', label: 'Company websites' },
    ];

    const toggleAiSource = (sourceValue) => {
        setSelectedAiSources((prev) => {
            if (sourceValue === 'abroadify') return prev;

            return prev.includes(sourceValue)
                ? prev.filter((item) => item !== sourceValue)
                : [...prev, sourceValue];
        });
    };

    const handleAiSubmit = async () => {
        const trimmedQuery = aiQuery.trim();

        if (!trimmedQuery || aiLoading) return;

        setAiLoading(true);
        setAiError('');
        setAiResult(null);

        try {
            const res = await api.post('/api/discovery-ai/query', {
                prompt: trimmedQuery,
                sources: selectedAiSources,
                discoveryType: PAGE_DISCOVERY_TYPE,
            });

            setAiResult({
                answer: res.data?.answer || '',
                suggestedFilters: Array.isArray(res.data?.suggestedFilters) ? res.data.suggestedFilters : [],
                structuredFilters: res.data?.structuredFilters || null,
                targetType: res.data?.targetType || PAGE_DISCOVERY_TYPE,
            });
        } catch (error) {
            console.error('Discovery AI query failed:', error);
            setAiError('Abroadify AI could not answer right now. Please try again.');
        } finally {
            setAiLoading(false);
        }
    };

    // This page already supports every field the AI can suggest, so every chip
    // applies locally.
    const handleChipClick = (sf) => {
        if (!sf) return;

        if (sf.country) {
            const iso2 = getCountryIso2ByName(sf.country);
            if (iso2) setSelectedCountry(iso2);
        }

        if (sf.city) setSelectedCity(sf.city);
        if (sf.industry) resolveIndustryLabel(sf.industry);

        if (sf.jobType === 'english-speaking' || sf.language === 'English') {
            setSelectedEnglishSpeaking(true);
        }

        if (sf.jobType === 'student') setSelectedStudentJobAreas(true);
        if (sf.jobType === 'internship') setSelectedInternship(true);
    };

    const industryNameByCode = useMemo(() => {
        const map = new Map();
        (industriesData || []).forEach((industry) => {
            if (industry?.code) {
                map.set(String(industry.code), industry.name || String(industry.code));
            }
        });
        return map;
    }, [industriesData]);

    // aiIndustryGroup is already a human-readable label; a selectedIndustry
    // NACE code is resolved to its label inside the helper.
    const mobileAiSuggestions = useMemo(() => getDiscoveryAiSuggestions({
        discoveryType: PAGE_DISCOVERY_TYPE,
        selectedCountry,
        selectedCity,
        selectedIndustry: aiIndustryGroup || selectedIndustry,
        selectedStudyDirection,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        industryNameByCode,
    }), [
        selectedCountry,
        selectedCity,
        aiIndustryGroup,
        selectedIndustry,
        selectedStudyDirection,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        industryNameByCode,
    ]);

    // Opt this page into the shared mobile AI assistant (header trigger +
    // bottom sheet). Desktop keeps the inline Ask Abroadify card below.
    const desktopAiInputRef = useDesktopAiInputRef();

    useMobileAiPageContext(
        {
            pageType: 'discovery-jobs',
            mapFirst: true,
            mapScope: 'global',
            discoveryType: PAGE_DISCOVERY_TYPE,
            suggestions: mobileAiSuggestions,
        },
        { onApplyStructuredFilters: handleChipClick }
    );

    const sortDefinitions = {
        name: { defaultDirection: 'asc', value: (company) => (company.title || '').toLowerCase(), type: 'text' },
        country: { defaultDirection: 'asc', value: (company) => (company.country || '').toLowerCase(), type: 'text' },
        city: { defaultDirection: 'asc', value: (company) => (company.cityRegion || '').toLowerCase(), type: 'text' },
        student: { defaultDirection: 'desc', value: (company) => Number(company.openJobsStudent) || 0, type: 'number' },
        intern: { defaultDirection: 'desc', value: (company) => Number(company.openJobsIntern) || 0, type: 'number' },
        oneToThree: { defaultDirection: 'desc', value: (company) => Number(company.openJobs13) || 0, type: 'number' },
        threePlus: { defaultDirection: 'desc', value: (company) => Number(company.openJobs3) || 0, type: 'number' },
        total: { defaultDirection: 'desc', value: (company) => Number(company.openJobsTotal) || 0, type: 'number' },
        lastUpdate: {
            defaultDirection: 'desc',
            value: (company) => new Date(company.latestDataUpdate || company.updatedAt || 0).getTime(),
            type: 'number',
        },
    };

    const getSortedCompanies = (sourceCompanies, field, direction) => {
        const definition = sortDefinitions[field];
        const multiplier = direction === 'asc' ? 1 : -1;

        refreshSortRef.current = (a, b) => {
            const aValue = definition.value(a);
            const bValue = definition.value(b);
            const comparison = definition.type === 'text'
                ? aValue.localeCompare(bValue)
                : aValue - bValue;
            return comparison * multiplier;
        };
        return [...sourceCompanies].sort(refreshSortRef.current);
    };

    const sortCompanies = (field) => {
        const direction = sortField === field
            ? (sortDirection === 'asc' ? 'desc' : 'asc')
            : sortDefinitions[field].defaultDirection;

        setCompanies((currentCompanies) => getSortedCompanies(currentCompanies, field, direction));
        setSortField(field);
        setSortDirection(direction);
    };

    const sortByName = () => sortCompanies('name');
    const sortByCountry = () => sortCompanies('country');
    const sortByCity = () => sortCompanies('city');
    const sortByStudent = () => sortCompanies('student');
    const sortByIntern = () => sortCompanies('intern');
    const sortBy13 = () => sortCompanies('oneToThree');
    const sortBy3 = () => sortCompanies('threePlus');
    const sortByTotal = () => sortCompanies('total');
    const sortByLastUpdate = () => sortCompanies('lastUpdate');

    const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);
    const noCompanies = filteredCompanies.length === 0;
    const hasActiveJobFilters = Boolean(
        searchQuery.trim() ||
        selectedCountry ||
        selectedCity ||
        selectedIndustry ||
        selectedStudyDirection ||
        selectedCompanySize ||
        selectedCompanyRating ||
        selectedEnglishSpeaking !== null ||
        selectedWorldTraveler !== null ||
        selectedStudentJobAreas !== null ||
        selectedInternship !== null ||
        selectedFavorites !== null ||
        Boolean(aiIndustryGroup)
    );

    const companiesWithCoordinates = filteredCompanies.filter((company) => {
        const lat = Number(company.latitude);
        const lng = Number(company.longitude);
        return !Number.isNaN(lat) && !Number.isNaN(lng);
    });

    const anyCompaniesWithCoordinates = companies.some((company) => {
        const lat = Number(company.latitude);
        const lng = Number(company.longitude);
        return !Number.isNaN(lat) && !Number.isNaN(lng);
    });

    // Mobile drill-down: page filters set the starting level, bubble taps go deeper.
    const mapDrillCountry = isMobileViewport ? String(mapDrill.country || selectedCountry || '').trim() : '';
    const mapDrillCity = isMobileViewport ? String(mapDrill.city || selectedCity || '').trim() : '';

    const mapCountryScope = mapDrillCountry
        ? companiesWithCoordinates.filter((company) => getCompanyCountryKey(company) === mapDrillCountry)
        : companiesWithCoordinates;

    const mapCityCount = new Set(mapCountryScope.map(getCompanyCityKey).filter(Boolean)).size;

    // country → city → company; a country whose companies sit in a single city
    // (or carry no city values) skips straight to the company markers.
    const mapAggregationMode = !isMobileViewport
        ? null
        : mapDrillCity
            ? 'company'
            : !mapDrillCountry
                ? 'country'
                : mapCityCount > 1 ? 'city' : 'company';

    const mapItems = mapAggregationMode === 'company' && mapDrillCity
        ? mapCountryScope.filter((company) => getCompanyCityKey(company) === mapDrillCity)
        : mapCountryScope;

    const mapFitBoundsPadding = isMobileViewport ? 60 : 0;
    const mapFitBoundsMaxZoom = !isMobileViewport
        ? null
        : mapDrillCity
            ? MOBILE_MAP_CITY_FIT_ZOOM
            : mapDrillCountry
                ? MOBILE_MAP_COUNTRY_FIT_ZOOM
                : MOBILE_MAP_OVERVIEW_MAX_ZOOM;

    const handleMapClusterClick = (cluster) => {
        if (mapAggregationMode === 'country') {
            setMapDrill({ country: cluster.groupKey, city: '' });
        } else if (mapAggregationMode === 'city') {
            setMapDrill((prev) => ({ ...prev, city: cluster.groupKey }));
        }
    };

    const handleMapZoomEnd = ({ zoom, isUserInteraction }) => {
        const prevZoom = mapPrevZoomRef.current;
        mapPrevZoomRef.current = zoom;
        // Only a genuine user zoom-out steps back — programmatic fits and pans don't.
        if (!isMobileViewport || !isUserInteraction || prevZoom == null || !(zoom < prevZoom)) return;
        if (zoom < MOBILE_MAP_COUNTRY_MODE_MIN_ZOOM && (mapDrill.country || mapDrill.city)) {
            setMapDrill({ country: '', city: '' });
        } else if (zoom < MOBILE_MAP_CITY_MODE_MIN_ZOOM && mapDrill.city) {
            setMapDrill((prev) => ({ ...prev, city: '' }));
        }
    };

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const getPaginationItems = () => {
        const siblingCount = 2;
        const boundaryCount = 1;
        if (totalPages <= 1) return [1];

        const range = (start, end) => {
            const out = [];
            for (let i = start; i <= end; i++) out.push(i);
            return out;
        };

        const startPages = range(1, Math.min(boundaryCount, totalPages));
        const endPages = range(
            Math.max(totalPages - boundaryCount + 1, boundaryCount + 1),
            totalPages
        );

        const siblingsStart = Math.max(
            Math.min(
                currentPage - siblingCount,
                totalPages - boundaryCount - siblingCount * 2 - 1
            ),
            boundaryCount + 2
        );

        const siblingsEnd = Math.min(
            Math.max(
                currentPage + siblingCount,
                boundaryCount + siblingCount * 2 + 2
            ),
            totalPages - boundaryCount - 1
        );

        const items = [
            ...startPages,
            ...(siblingsStart > boundaryCount + 2
                ? ['ellipsis']
                : boundaryCount + 1 < totalPages - boundaryCount ? [boundaryCount + 1] : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundaryCount - 1
                ? ['ellipsis']
                : totalPages - boundaryCount > boundaryCount ? [totalPages - boundaryCount] : []),
            ...endPages,
        ];

        return items.filter((v, i) => items.indexOf(v) === i);
    };

    const discoveryNavItems = [
        { to: '/discovery/countries', eventKey: 'countries', label: 'Countries' },
        { to: '/discovery/cities', eventKey: 'cities', label: 'Cities' },
        { to: '/discovery/companies/jobs', eventKey: 'companies', label: 'Companies & Jobs', alsoActive: ['/discovery/companies', '/discovery/jobs'] },
    ];

    const filterBarProps = {
        title: 'Jobs',
        viewOptions: [
            { value: 'companies', label: 'Companies' },
            { value: 'jobs', label: 'Jobs' },
        ],
        activeView: 'jobs',
        onViewChange: (view) => {
            if (view === 'companies') {
                trackEvent(ANALYTICS_EVENTS.DISCOVERY_VIEW_SWITCHED, {
                    discoveryType: 'companies',
                    fromViewMode: 'jobs',
                    toViewMode: 'companies',
                    level: 1,
                    source: 'companies_jobs_toggle',
                });

                navigate('/discovery/companies');
            }
        },
        items: companies,
        allowedCities: activeCityNames,
        industryNameByCode,
        cityField: 'cityRegion',
        searchQuery,
        setSearchQuery,
        selectedCountry,
        setSelectedCountry,
        selectedCity,
        setSelectedCity,
        selectedIndustry,
        setSelectedIndustry,
        selectedStudyDirection,
        setSelectedStudyDirection,
        selectedCompanySize,
        setSelectedCompanySize,
        selectedCompanyRating,
        setSelectedCompanyRating,
        selectedEnglishSpeaking,
        setSelectedEnglishSpeaking,
        selectedWorldTraveler,
        setSelectedWorldTraveler,
        selectedStudentJobAreas,
        setSelectedStudentJobAreas,
        aiIndustryGroupLabel: aiIndustryGroup,
        onClearAiIndustryGroup: clearAiIndustryGroup,
        selectedFavorites,
        setSelectedFavorites,
        savedFilterSets,
        selectedFilterSet,
        onFilterSetChange: handleFilterSetChange,
        onDeleteFilterSet: handleDeleteFilterSet,
        onSaveFilters: handleSaveFilters,
        onClearFilters: clearJobsFilters,
    };

    const captureCurrentMobileFilters = () => captureDiscoveryFilters({
        searchQuery,
        selectedCountry,
        selectedCity,
        selectedIndustry,
        aiIndustryGroup,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    });

    const restoreMobileFilters = (filters) => {
        setSelectedCountry(filters.selectedCountry);
        setSelectedCity(filters.selectedCity);
        setSelectedIndustry(filters.selectedIndustry);
        setAiIndustryGroup(filters.aiIndustryGroup);
        setSelectedStudyDirection(filters.selectedStudyDirection);
        setSelectedCompanySize(filters.selectedCompanySize);
        setSelectedCompanyRating(filters.selectedCompanyRating);
        setSelectedEnglishSpeaking(filters.selectedEnglishSpeaking);
        setSelectedWorldTraveler(filters.selectedWorldTraveler);
        setSelectedStudentJobAreas(filters.selectedStudentJobAreas);
        setSelectedInternship(filters.selectedInternship);
        setSelectedFavorites(filters.selectedFavorites);
    };

    const closeMobileFilters = () => {
        restoreMobileFilters(appliedMobileFilters);
        setShowMobileFilters(false);
    };

    const applyMobileFilters = () => {
        setAppliedMobileFilters(captureCurrentMobileFilters());
        setShowMobileFilters(false);
    };

    const removeAppliedMobileFilter = (key) => {
        const clearedValue = key.startsWith('selected') && [
            'selectedEnglishSpeaking',
            'selectedWorldTraveler',
            'selectedStudentJobAreas',
            'selectedInternship',
            'selectedFavorites',
        ].includes(key) ? null : (key === 'aiIndustryGroup' ? null : '');
        const nextFilters = { ...appliedMobileFilters, [key]: clearedValue };
        restoreMobileFilters(nextFilters);
        setAppliedMobileFilters(nextFilters);
    };

    const mobileSortOptions = [
        { field: 'name', label: 'Company' },
        { field: 'country', label: 'Country' },
        { field: 'city', label: 'City' },
        { field: 'student', label: 'Student jobs' },
        { field: 'intern', label: 'Internships' },
        { field: 'oneToThree', label: '1-3 years' },
        { field: 'threePlus', label: '3+ years' },
        { field: 'total', label: 'Total jobs' },
        { field: 'lastUpdate', label: 'Last update' },
    ];
    const activeMobileSortOption = mobileSortOptions.find((option) => option.field === sortField);

    const clearMobileSort = () => {
        refreshSortRef.current = null;
        setCompanies([...defaultCompaniesOrderRef.current]);
        setSortField(null);
        setSortDirection(null);
    };

    const openMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(true);
    };

    const closeMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(false);
    };

    const clearMobileSortFromDrawer = () => {
        clearMobileSort();
        setDraftSortField(null);
        setDraftSortDirection(null);
        setShowMobileSort(false);
    };

    const selectDraftSort = (field) => {
        setDraftSortDirection((currentDirection) => (
            draftSortField === field
                ? (currentDirection === 'asc' ? 'desc' : 'asc')
                : sortDefinitions[field].defaultDirection
        ));
        setDraftSortField(field);
    };

    const applyMobileSort = () => {
        if (draftSortField) {
            setCompanies(getSortedCompanies(defaultCompaniesOrderRef.current, draftSortField, draftSortDirection));
        } else {
            refreshSortRef.current = null;
            setCompanies([...defaultCompaniesOrderRef.current]);
        }

        setSortField(draftSortField);
        setSortDirection(draftSortDirection);
        setShowMobileSort(false);
    };

    const appliedMobileChips = buildDiscoveryFilterChipLabels(appliedMobileFilters, industryNameByCode)
        .map((chip) => ({
            ...chip,
            onRemove: () => removeAppliedMobileFilter(chip.key),
        }));

    if (activeMobileSortOption) {
        appliedMobileChips.push({
            key: 'activeSort',
            label: activeMobileSortOption.label,
            icon: sortDirection === 'asc'
                ? <FaCaretUp aria-hidden="true" />
                : <FaCaretDown aria-hidden="true" />,
            onOpen: openMobileSort,
            onRemove: clearMobileSort,
            removeLabel: `Clear ${activeMobileSortOption.label} sort`,
        });
    }

    const toggleFavorite = async (companyId) => {
        const companyIdStr = String(companyId);
        const previousFavorites = new Set(favorites);
        const wasFavorited = previousFavorites.has(companyIdStr);

        const optimisticFavorites = new Set(previousFavorites);
        if (wasFavorited) {
            optimisticFavorites.delete(companyIdStr);
        } else {
            optimisticFavorites.add(companyIdStr);
        }
        setFavorites(optimisticFavorites);

        try {
            const res = await api.patch(`/api/users/me/favorite-companies/${companyIdStr}`);
            const safeFavoriteCompanies = Array.isArray(res?.data?.favoriteCompanies)
                ? res.data.favoriteCompanies.map((id) => String(id))
                : [];
            setFavorites(new Set(safeFavoriteCompanies));

            const company = companies.find((c) => String(c._id) === companyIdStr);
            trackEvent(
                wasFavorited ? ANALYTICS_EVENTS.FAVORITE_COMPANY_REMOVED : ANALYTICS_EVENTS.FAVORITE_COMPANY_ADDED,
                {
                    companyId: companyIdStr,
                    companyName: company?.title || '',
                    level: 1,
                    source: 'jobs_list',
                }
            );
        } catch (error) {
            console.error('Error toggling favorite company:', error);
            setFavorites(previousFavorites);
        }
    };

    const handleCopy = async (text, key) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setShowCopied(false);
            if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
            requestAnimationFrame(() => {
                setShowCopied(true);
                copyTimeoutRef.current = setTimeout(() => {
                    setShowCopied(false);
                    copyTimeoutRef.current = null;
                }, 1200);
            });
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };

    const renderJobCount = (studentValue, companyValue = 0) => {
        const student = Number(studentValue) || 0;
        const company = Number(companyValue) || 0;
        if (!student && !company) return <span className="text-muted">n/a</span>;
        if (company) {
            return (
                <span className="fw-bold">
                    {company} <span className="text-muted fw-normal">({student})</span>
                </span>
            );
        }
        return <span className="text-primary fw-bold">{student}</span>;
    };

    const renderAiAssistant = () => (
        <DiscoveryAiDesktopCard
            aiQuery={aiQuery}
            setAiQuery={setAiQuery}
            aiLoading={aiLoading}
            aiResult={aiResult}
            aiError={aiError}
            sourceOptions={aiSourceOptions}
            selectedAiSources={selectedAiSources}
            toggleAiSource={toggleAiSource}
            onSubmit={handleAiSubmit}
            onApplyFilters={handleChipClick}
            inputRef={desktopAiInputRef}
        />
    );

    return (
        <>
            <Sidebar />
            <main className={`main-content flex-grow-1 discovery-jobs-page discovery-map-page${isMobileNavDocked ? ' is-mobile-nav-docked' : ''}`}>
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col>
                            <Card className="discovery-shell-card">
                                <Card.Body>
                                    <Row className="discovery-page-title-row">
                                        <Col xs={12}>
                                            <h1>Discover</h1>
                                        </Col>
                                    </Row>

                                    {anyCompaniesWithCoordinates && (
                                        <Row ref={mobileMapRowRef} className="discovery-map-row">
                                            <Col xs={12} className="px-0">
                                                <div className="discovery-mobile-map-area">
                                                    <DiscoveryMap
                                                        items={mapItems}
                                                        height={260}
                                                        initialCenter={mapInitialCenter}
                                                        fitBoundsPadding={mapFitBoundsPadding}
                                                        fitBoundsMaxZoom={mapFitBoundsMaxZoom}
                                                        groupBy={mapAggregationMode === 'country'
                                                            ? getCompanyCountryKey
                                                            : mapAggregationMode === 'city' ? getCompanyCityKey : null}
                                                        groupClickZoom={mapAggregationMode === 'city'
                                                            ? MOBILE_MAP_CITY_FIT_ZOOM
                                                            : mapAggregationMode === 'company' ? MOBILE_MAP_COMPANY_CLUSTER_CLICK_ZOOM : 8}
                                                        maxClusterZoom={mapAggregationMode === 'company' ? MOBILE_MAP_COMPANY_UNCLUSTER_ZOOM : null}
                                                        onClusterClick={handleMapClusterClick}
                                                        onZoomEnd={handleMapZoomEnd}
                                                        getLatitude={(company) => company.latitude}
                                                        getLongitude={(company) => company.longitude}
                                                        getTitle={(company) => company.title}
                                                        getLogo={(company) => company.logo}
                                                        getLink={(company) => `/discovery/companies/${company._id}/jobs`}
                                                        onLinkClick={(company) => trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                            companyId: company._id,
                                                            companyName: company.title,
                                                            source: 'jobs_map',
                                                            level: 1,
                                                            viewMode: 'jobs',
                                                        })}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className="mb-4 discovery-tabs-row">
                                        <Col>
                                            <NavTabs
                                                items={discoveryNavItems}
                                                className="discovery-route-tabs"
                                                autoRevealActive
                                                trackOverflow
                                            />
                                        </Col>
                                    </Row>

                                    {!isMobile && (
                                    <Row className="mb-4">
                                        <Col>
                                            {renderAiAssistant()}
                                        </Col>
                                    </Row>
                                    )}

                                    {!isMobile && (
                                    <Row className="mb-3">
                                        <Col xs={12}>
                                            <DiscoveryFilterBar
                                                {...filterBarProps}
                                            />
                                        </Col>
                                    </Row>
                                    )}

                                    {isMobile && (
                                        <>
                                            <MobileDiscoveryViewSwitch activeView="jobs" />

                                            <MobileDiscoveryHeading title="Jobs" />

                                            <MobileDiscoverySearchToolbar
                                                searchValue={searchQuery}
                                                onSearchChange={setSearchQuery}
                                                searchPlaceholder="Search..."
                                                searchAriaLabel="Search jobs"
                                                actions={[
                                                    {
                                                        label: 'Open filters',
                                                        icon: icFilter,
                                                        onClick: () => setShowMobileFilters(true),
                                                    },
                                                    {
                                                        label: 'Sort jobs',
                                                        icon: icSort,
                                                        onClick: openMobileSort,
                                                    },
                                                ]}
                                            />

                                            <MobileAppliedChips chips={appliedMobileChips} />

                                            <DiscoveryFilterBar
                                                {...filterBarProps}
                                                onClearFilters={clearJobsFiltersPreserveSearch}
                                                onDeleteFilterSet={(filterSetId) => handleDeleteFilterSet(filterSetId, { preserveSearch: true })}
                                                mobileModalOnly
                                                mobileModalOpen={showMobileFilters}
                                                onMobileModalClose={closeMobileFilters}
                                                onMobileModalApply={applyMobileFilters}
                                                mobileModalTitle="Filter jobs"
                                                mobileApplyLabel="Apply filters"
                                                hideTypeToggleOnMobile
                                                hideActiveFilterSummaryOnMobile
                                                useFullScreenMobilePanel
                                                mobileSavedFilterLabel="Saved filter"
                                                mobileSaveFilterLabel="Save current filters"
                                                compactSavedFilterOnMobile
                                                hideSearchInMobileModal
                                            />
                                        </>
                                    )}

                                    {noCompanies && isMobile ? (
                                        <DiscoveryEmptyState
                                            className="discovery-empty-state--mobile-jobs"
                                            icon={icJobSearch}
                                            title={hasActiveJobFilters ? 'No matching jobs found' : 'No jobs available'}
                                            description={
                                                hasActiveJobFilters
                                                    ? 'Try removing one or more filters or adjust your search.'
                                                    : 'There are currently no jobs available for this selection. You can explore companies instead.'
                                            }
                                            primaryAction={
                                                hasActiveJobFilters
                                                    ? {
                                                        label: 'Clear search & filters',
                                                        variant: 'primary',
                                                        onClick: clearJobsFilters,
                                                    }
                                                    : {
                                                        label: 'View companies',
                                                        variant: 'primary',
                                                        onClick: () => navigate('/discovery/companies'),
                                                    }
                                            }
                                            secondaryAction={
                                                hasActiveJobFilters
                                                    ? {
                                                        label: 'Adjust filters',
                                                        variant: 'outline-secondary',
                                                        onClick: () => setShowMobileFilters(true),
                                                    }
                                                    : null
                                            }
                                        />
                                    ) : noCompanies ? (
                                        <p className="mb-0">No jobs available.</p>
                                    ) : isMobile ? (
                                        <div className="mobile-company-list">
                                            {mobilePagination.mobileItems.map((company) => (
                                                <MobileCompanyCard
                                                    key={company._id}
                                                    company={company}
                                                    isFavorite={favorites.has(String(company._id))}
                                                    onFavoriteToggle={toggleFavorite}
                                                    onOpenCompany={(selectedCompany) => {
                                                        mobilePagination.rememberScrollPosition();
                                                        trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                            companyId: selectedCompany._id,
                                                            companyName: selectedCompany.title,
                                                            source: 'jobs_list',
                                                            level: 1,
                                                            viewMode: 'jobs',
                                                        });
                                                        navigate(`/discovery/companies/${selectedCompany._id}/jobs`);
                                                    }}
                                                />
                                            ))}
                                            <MobileLoadMoreFooter
                                                visibleCount={mobilePagination.visibleCount}
                                                totalCount={mobilePagination.totalCount}
                                                itemLabel="jobs"
                                                onLoadMore={mobilePagination.loadMore}
                                                isLoading={mobilePagination.isLoadingMore}
                                                hasMore={mobilePagination.hasMore}
                                            />
                                        </div>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table className="discovery-table">
                                                        <thead>
                                                            <tr>
                                                                <th onClick={sortByName} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Company
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortByCountry} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Country
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortByCity} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        City
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th>
                                                                    <div>HR contact</div>
                                                                </th>
                                                                <th>
                                                                    <div>Recruiting portal</div>
                                                                </th>
                                                                <th onClick={sortByStudent} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Student jobs
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortByIntern} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Intern
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortBy13} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        1–3 year
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortBy3} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        3+ year
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortByTotal} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Total
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortByLastUpdate} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Last Update
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th>Fav</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentCompanies.map((company) => (
                                                                <tr
                                                                    className="company-row"
                                                                    key={company._id}
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => {
                                                                        trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                                            companyId: company._id,
                                                                            companyName: company.title,
                                                                            source: 'jobs_list',
                                                                            level: 1,
                                                                            viewMode: 'jobs',
                                                                        });
                                                                        navigate(`/discovery/companies/${company._id}/jobs`);
                                                                    }}
                                                                >
                                                                    <td>
                                                                        <div>
                                                                            <img
                                                                                src={company.logo ? company.logo : defaultLogo}
                                                                                alt={company.title}
                                                                                className="round"
                                                                            />
                                                                            <span className="text-ellipsis text-ellipsis-country-name">
                                                                                <strong>{company.title}</strong>
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td>{company.country || <span className="text-muted">n/a</span>}</td>
                                                                    <td>{company.cityRegion || <span className="text-muted">n/a</span>}</td>
                                                                    <td>
                                                                        <div className="d-inline-flex align-items-center gap-2">
                                                                            {company.hrEmail ? (
                                                                                <span
                                                                                    ref={(el) => { contactRefs.current[`${company._id}-email`] = el; }}
                                                                                    className="d-inline-flex align-items-center"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    onMouseEnter={() => setOverlayTarget({ el: contactRefs.current[`${company._id}-email`], key: `${company._id}-email` })}
                                                                                    onMouseLeave={() => { if (!showCopied) setOverlayTarget(null); }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setOverlayTarget({ el: contactRefs.current[`${company._id}-email`], key: `${company._id}-email` });
                                                                                        handleCopy(company.hrEmail);
                                                                                    }}
                                                                                >
                                                                                    <img src={icMail} alt="email" style={{ width: '16px', height: '16px' }} />
                                                                                </span>
                                                                            ) : null}
                                                                            {company.hrPhone ? (
                                                                                <span
                                                                                    ref={(el) => { contactRefs.current[`${company._id}-phone`] = el; }}
                                                                                    className="d-inline-flex align-items-center"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    onMouseEnter={() => setOverlayTarget({ el: contactRefs.current[`${company._id}-phone`], key: `${company._id}-phone` })}
                                                                                    onMouseLeave={() => { if (!showCopied) setOverlayTarget(null); }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setOverlayTarget({ el: contactRefs.current[`${company._id}-phone`], key: `${company._id}-phone` });
                                                                                        handleCopy(String(company.hrPhone));
                                                                                    }}
                                                                                >
                                                                                    <img src={icPhone} alt="phone" style={{ width: '16px', height: '16px' }} />
                                                                                </span>
                                                                            ) : null}
                                                                            {!company.hrEmail && !company.hrPhone ? <span className="text-muted">n/a</span> : null}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        {company.hrRecrutingUrl ? (
                                                                            <a
                                                                                href={company.hrRecrutingUrl.startsWith('http') ? company.hrRecrutingUrl : `https://${company.hrRecrutingUrl}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="d-inline-flex align-items-center"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    trackEvent(ANALYTICS_EVENTS.COMPANY_WEBSITE_CLICKED, {
                                                                                        companyId: company._id,
                                                                                        companyName: company.title,
                                                                                        urlType: 'recruiting_portal',
                                                                                        level: 1,
                                                                                        source: 'jobs_list',
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <img src={icLink} alt="recruiting portal" style={{ width: '16px', height: '16px' }} />
                                                                            </a>
                                                                        ) : (
                                                                            <span className="text-muted">n/a</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{renderJobCount(company.openJobsStudent)}</td>
                                                                    <td>{renderJobCount(company.openJobsIntern)}</td>
                                                                    <td>{renderJobCount(company.openJobs13)}</td>
                                                                    <td>{renderJobCount(company.openJobs3)}</td>
                                                                    <td>{renderJobCount(company.openJobsTotal)}</td>
                                                                    <td>
                                                                        <span className="text-muted small">
                                                                            {company.latestDataUpdate || company.updatedAt
                                                                                ? new Date(company.latestDataUpdate || company.updatedAt).toLocaleDateString()
                                                                                : '—'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            style={{ cursor: 'pointer' }}
                                                                            className="d-inline-flex align-items-center"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFavorite(company._id);
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={favorites.has(String(company._id)) ? icHeartFull : icHeartEmpty}
                                                                                alt="favorite"
                                                                                style={{ width: '16px', height: '16px' }}
                                                                            />
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {!isMobile && (
                                    <Row className="pagination-wrapper">
                                        <Col className="d-flex align-items-center">
                                            <span>
                                                Total {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
                                            </span>
                                            <Pagination>
                                                <Pagination.Prev
                                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                >
                                                    <BsChevronLeft />
                                                </Pagination.Prev>

                                                {getPaginationItems().map((item, index) =>
                                                    item === 'ellipsis' ? (
                                                        <Pagination.Ellipsis key={`e-${index}`} disabled />
                                                    ) : (
                                                        <Pagination.Item
                                                            key={item}
                                                            active={item === currentPage}
                                                            onClick={() => handlePageChange(item)}
                                                        >
                                                            {item}
                                                        </Pagination.Item>
                                                    )
                                                )}

                                                <Pagination.Next
                                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages || totalPages === 0}
                                                >
                                                    <BsChevronRight />
                                                </Pagination.Next>
                                            </Pagination>
                                        </Col>

                                        <Col className="pagination-controls">
                                            <div className="pagination-controls-per-page">
                                                <Form.Select
                                                    value={companiesPerPage}
                                                    onChange={(e) => {
                                                        setCompaniesPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value="10">10 / page</option>
                                                    <option value="20">20 / page</option>
                                                    <option value="40">40 / page</option>
                                                </Form.Select>
                                            </div>

                                            <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                                Go to
                                            </label>

                                            <Form.Control
                                                className="pagination-controls-goto-input"
                                                type="number"
                                                value={currentPage}
                                                onChange={(e) => {
                                                    const newPage = Number(e.target.value);
                                                    if (newPage >= 1 && newPage <= totalPages) {
                                                        setCurrentPage(newPage);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (currentPage < 1) setCurrentPage(1);
                                                    if (currentPage > totalPages) setCurrentPage(totalPages);
                                                }}
                                            />
                                        </Col>
                                    </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>

            <MobileJobsSortDrawer
                showMobileSort={showMobileSort}
                closeMobileSort={closeMobileSort}
                mobileSortOptions={mobileSortOptions}
                draftSortField={draftSortField}
                draftSortDirection={draftSortDirection}
                selectDraftSort={selectDraftSort}
                clearMobileSortFromDrawer={clearMobileSortFromDrawer}
                applyMobileSort={applyMobileSort}
            />

            <Overlay
                target={overlayTarget?.el}
                show={!!overlayTarget}
                placement="top"
                container={document.body}
                popperConfig={{ strategy: 'fixed' }}
            >
                {(props) => (
                    <Tooltip id="contact-copy-tooltip" {...props}>
                        {showCopied ? 'Copied!' : (
                            overlayTarget?.key?.endsWith('-email') ? 'Copy email' : 'Copy phone'
                        )}
                    </Tooltip>
                )}
            </Overlay>
        </>
    );
};

export default DiscoveryJobsList;
